import { useEffect, useRef } from 'react'
import type { RefObject } from 'react'
import { BUILTIN_STATUS_LIST } from '@/lib/statusRegistry'
import { StatusChipIcon } from '@/components/StatusMenu'

interface TokenStatusPopoverProps {
  /** Fixed-position anchor for the popover (screen coords). */
  position: { x: number; y: number }
  /** Currently active status keys on this token. */
  activeStatuses: string[]
  /** Toggle a single condition on/off. Popover stays open after each toggle. */
  onToggle: (key: string) => void
  /** Close the popover (called on outside-click). */
  onClose: () => void
  /**
   * Optional ref to the element that triggered the popover. Included in the
   * "inside" zone so a click on the trigger doesn't immediately fire the
   * outside-click close. The on-canvas lightning grip is a PIXI object, not a
   * DOM node, so this is usually undefined here — outside-click still works.
   */
  triggerRef?: RefObject<HTMLElement>
}

// Layout per Mike's spec (Figma 3246-425): strictly status ICONS with hover
// tooltips — NO header text, NO Clear All. Grid 4 wide × 3 high = 12 visible;
// scroll past 12.
const COLS = 4
const VISIBLE_ROWS = 3
const CHIP_PX = 32 // chip edge
const GAP_PX = 4

// Cap the grid to exactly 3 rows so a >12 status list scrolls inside the panel.
const GRID_MAX_HEIGHT = VISIBLE_ROWS * CHIP_PX + (VISIBLE_ROWS - 1) * GAP_PX

/**
 * On-token status picker popover (lightning-grip target).
 *
 * Adapted from StatusMenu to Mike's verbatim spec: icons + hover tooltips only,
 * 4×3 grid, scrollable past 12, multi-select (stays open per toggle), closes on
 * outside-click. Uses the existing `.ac-hud-card` Grimoire surface and the mint
 * status icons from src/render/statusIcons.ts.
 */
export function TokenStatusPopover({
  position,
  activeStatuses,
  onToggle,
  onClose,
  triggerRef,
}: TokenStatusPopoverProps) {
  const menuRef = useRef<HTMLDivElement>(null)
  const pointerDownOutside = useRef(false)

  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    const isInside = (target: EventTarget | null): boolean => {
      if (!(target instanceof Node)) return false
      return !!(
        menuRef.current?.contains(target) ||
        triggerRef?.current?.contains(target)
      )
    }

    // Press-AND-release pattern: both pointerdown and pointerup must land
    // outside before closing. A bare `click` listener would close on an
    // inner-chip re-render (React recreates the clicked node before the click
    // bubbles to document, making contains() return false for an inside click).
    const onPointerDown = (e: PointerEvent) => {
      pointerDownOutside.current = !isInside(e.target)
    }
    const onPointerUp = (e: PointerEvent) => {
      if (pointerDownOutside.current && !isInside(e.target)) {
        onCloseRef.current()
      }
      pointerDownOutside.current = false
    }

    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('pointerup', onPointerUp, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('pointerup', onPointerUp, true)
    }
    // triggerRef identity is stable (same useRef object across renders).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Width = 4 chips + 3 gaps + the .ac-hud-card padding (0.5rem each side).
  const gridWidth = COLS * CHIP_PX + (COLS - 1) * GAP_PX

  return (
    <div
      ref={menuRef}
      className="ac-hud-card"
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        padding: '0.5rem',
        zIndex: 300,
        pointerEvents: 'auto',
      }}
      // Prevent right-clicks on the popover from opening the canvas context menu.
      onContextMenu={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      {/* 4-wide × 3-high status-icon grid, height-capped + scrollable past 12. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${COLS}, ${CHIP_PX}px)`,
          gap: GAP_PX,
          width: gridWidth,
          maxHeight: GRID_MAX_HEIGHT,
          overflowY: 'auto',
        }}
      >
        {BUILTIN_STATUS_LIST.map((s) => {
          const active = activeStatuses.includes(s.key)
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => onToggle(s.key)}
              title={s.name}
              aria-label={s.name}
              aria-pressed={active}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: CHIP_PX,
                height: CHIP_PX,
                padding: 0,
                borderRadius: 6,
                border: active
                  ? '1.5px solid hsl(var(--ac-gm-gold, 45 80% 50%) / 0.65)'
                  : '1px solid hsl(var(--ac-dark-accent))',
                background: active
                  ? 'hsl(var(--ac-gm-gold, 45 80% 50%) / 0.22)'
                  : 'hsl(var(--ac-dark-primary) / 0.5)',
                cursor: 'pointer',
                transition: 'background 80ms, border-color 80ms',
              }}
            >
              <StatusChipIcon status={s} />
            </button>
          )
        })}
      </div>
    </div>
  )
}
